import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "providers/ToastProvider";
import { authedFetch } from "lib/utils/authedFetch";

type PromptsPayload = {
    items?: unknown;
    prompt?: unknown;
    threshold?: unknown;
};

export type OpenVocabObjectRow = {
    text: string;
    threshold: number;
};

export type OpenVocabPromptsPanelProps = {
    /** Absolute URL for GET/PUT /prompts, or null when host/proxy unavailable. */
    getPromptsUrl: () => string | null;
    /** Remount / reload when this changes (e.g. active model filename). */
    reloadKey?: string;
};

/** Match YOLO class minimum-confidence slider styling. */
const MIN_CONF_RANGE_CLASS =
    "h-4 w-full min-w-[6rem] flex-1 cursor-pointer bg-transparent [color-scheme:dark] " +
    "appearance-none accent-primary2 outline-none disabled:cursor-not-allowed disabled:opacity-50 " +
    "[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full " +
    "[&::-webkit-slider-runnable-track]:bg-black70 " +
    "[&::-webkit-slider-thumb]:-mt-0.5 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 " +
    "[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none " +
    "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 " +
    "[&::-webkit-slider-thumb]:bg-primary2 " +
    "[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-black70 " +
    "[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:cursor-pointer " +
    "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary2";

function clamp01(x: number, fallback = 0.1): number {
    if (!Number.isFinite(x)) return fallback;
    return Math.max(0, Math.min(1, x));
}

/** Match Python `_parse_prompt`: strip leading/trailing `] [ ( )` then split on commas. */
function parsePromptBracketString(prompt: string): string[] {
    let s = prompt.trim();
    const stripChars = new Set("[]()");
    while (s.length && stripChars.has(s[0])) s = s.slice(1).trimStart();
    while (s.length && stripChars.has(s[s.length - 1])) s = s.slice(0, -1).trimEnd();
    return s
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
}

function parseItemsFromPayload(data: PromptsPayload): string[] {
    if (Array.isArray(data.items)) {
        return data.items.map((x) => String(x).trim()).filter(Boolean);
    }
    if (typeof data.prompt === "string" && data.prompt.trim()) {
        return parsePromptBracketString(data.prompt.trim());
    }
    return [];
}

/** One threshold per object row; repeats last or default to fill `n` slots. */
function parsePerObjectThresholds(raw: unknown, n: number): number[] {
    const def = 0.1;
    if (n <= 0) return [];
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return Array.from({ length: n }, () => clamp01(raw, def));
    }
    if (typeof raw === "string") {
        const parts = raw
            .split(",")
            .map((s) => parseFloat(s.trim()))
            .filter((x) => Number.isFinite(x))
            .map((x) => clamp01(x, def));
        if (parts.length === 0) return Array.from({ length: n }, () => def);
        return Array.from({ length: n }, (_, i) => parts[Math.min(i, parts.length - 1)]!);
    }
    if (Array.isArray(raw)) {
        const parts = raw
            .map((x) => parseFloat(String(x)))
            .filter((x) => Number.isFinite(x))
            .map((x) => clamp01(x, def));
        if (parts.length === 0) return Array.from({ length: n }, () => def);
        return Array.from({ length: n }, (_, i) => parts[Math.min(i, parts.length - 1)]!);
    }
    return Array.from({ length: n }, () => def);
}

function rowsFromPayload(data: PromptsPayload): OpenVocabObjectRow[] {
    const items = parseItemsFromPayload(data);
    const th = parsePerObjectThresholds(data.threshold, items.length);
    if (items.length === 0) return [{ text: "", threshold: 0.1 }];
    return items.map((text, i) => ({
        text,
        threshold: th[i] ?? 0.1,
    }));
}

const OpenVocabPromptsPanel: React.FC<OpenVocabPromptsPanelProps> = ({
    getPromptsUrl,
    reloadKey,
}) => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();

    const [rows, setRows] = useState<OpenVocabObjectRow[]>([{ text: "", threshold: 0.1 }]);
    const [promptsLoading, setPromptsLoading] = useState(false);
    const [promptsSaving, setPromptsSaving] = useState(false);

    const loadPrompts = useCallback(async () => {
        setPromptsLoading(true);
        try {
            const url = getPromptsUrl();
            if (!url) throw new Error("Fleet proxy host is unavailable");
            const res = await authedFetch(url);
            const text = await res.text();
            if (!res.ok) {
                throw new Error(t("objectRecognition.openVocabLoadFailed"));
            }
            const data = JSON.parse(text) as PromptsPayload;
            setRows(rowsFromPayload(data));
        } catch {
            addToast({
                type: "error",
                message: t("objectRecognition.openVocabLoadFailed"),
            });
        } finally {
            setPromptsLoading(false);
        }
    }, [getPromptsUrl, addToast, t]);

    useEffect(() => {
        void loadPrompts();
    }, [loadPrompts, reloadKey]);

    const addRow = useCallback(() => {
        setRows((prev) => [...prev, { text: "", threshold: 0.1 }]);
    }, []);

    const removeRow = useCallback((index: number) => {
        setRows((prev) =>
            prev.length <= 1 ? [{ text: "", threshold: 0.1 }] : prev.filter((_, i) => i !== index)
        );
    }, []);

    const updateRowText = useCallback((index: number, value: string) => {
        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, text: value } : row)));
    }, []);

    const updateRowThreshold = useCallback((index: number, value: number) => {
        const v = clamp01(value, 0.1);
        setRows((prev) =>
            prev.map((row, i) => (i === index ? { ...row, threshold: v } : row))
        );
    }, []);

    const savePrompts = useCallback(async () => {
        const trimmed = rows
            .map((r) => ({ text: r.text.trim(), threshold: clamp01(r.threshold, 0.1) }))
            .filter((r) => r.text.length > 0);
        if (trimmed.length === 0) {
            addToast({ type: "error", message: t("objectRecognition.openVocabNeedObject") });
            return;
        }
        const body = {
            items: trimmed.map((r) => r.text),
            threshold: trimmed.map((r) => r.threshold),
            reload_on_change: true,
        };
        setPromptsSaving(true);
        try {
            const url = getPromptsUrl();
            if (!url) throw new Error("Fleet proxy host is unavailable");
            const res = await authedFetch(url, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                let detail = t("objectRecognition.saveFailed");
                try {
                    const err = (await res.json()) as { error?: string };
                    if (err.error) detail = err.error;
                } catch {
                    /* ignore */
                }
                throw new Error(detail);
            }
            addToast({
                type: "success",
                message: t("objectRecognition.openVocabSaved"),
            });
            await loadPrompts();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("objectRecognition.saveFailed")),
            });
        } finally {
            setPromptsSaving(false);
        }
    }, [getPromptsUrl, rows, loadPrompts, addToast, t]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex w-full max-w-md flex-col gap-3 rounded-r10 border border-black80 bg-black90 p-3">
                <div>
                    <p className="text-h5 font-semibold text-white">
                        {t("objectRecognition.openVocabTitle")}
                    </p>
                    <p className="mt-0.5 text-h5 leading-snug text-black40">
                        {t("objectRecognition.openVocabDescription")}
                    </p>
                </div>

                <ul className="flex flex-col gap-2">
                    {rows.map((row, index) => (
                        <li
                            key={index}
                            className="flex w-full flex-col gap-1.5 rounded-r5 border border-black80 bg-black70/40 px-2 py-1.5"
                        >
                            <div className="flex flex-col gap-0.5">
                                <label
                                    htmlFor={`openvocab-obj-${index}`}
                                    className="text-h5 leading-none text-black40"
                                >
                                    {t("objectRecognition.openVocabItemLabel")}
                                </label>
                                <input
                                    id={`openvocab-obj-${index}`}
                                    type="text"
                                    value={row.text}
                                    onChange={(e) => updateRowText(index, e.target.value)}
                                    disabled={promptsLoading}
                                    placeholder={t("objectRecognition.openVocabItemPlaceholder")}
                                    className="w-full max-w-[11rem] rounded-r5 border border-black80 bg-black70 px-2 py-1.5 text-h5 leading-snug text-white placeholder:text-black60 focus:border-primary2 focus:outline-none disabled:opacity-50"
                                    aria-label={t("objectRecognition.openVocabItemAria", {
                                        index: index + 1,
                                    })}
                                />
                            </div>
                            <div className="flex max-w-full items-center gap-2">
                                <span className="shrink-0 text-h5 leading-none text-black40">
                                    {t("objectRecognition.openVocabConfidence")}
                                </span>
                                <input
                                    type="range"
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={row.threshold}
                                    disabled={promptsLoading}
                                    onChange={(e) => {
                                        const v = parseFloat(e.target.value);
                                        if (Number.isFinite(v)) {
                                            updateRowThreshold(index, v);
                                        }
                                    }}
                                    className={`${MIN_CONF_RANGE_CLASS} min-w-0 flex-1`}
                                    aria-label={t("objectRecognition.openVocabConfidenceAria", {
                                        index: index + 1,
                                    })}
                                />
                                <span className="w-9 shrink-0 tabular-nums text-h5 leading-none text-black40">
                                    {row.threshold.toFixed(2)}
                                </span>
                                <button
                                    type="button"
                                    disabled={promptsLoading || (rows.length === 1 && row.text === "")}
                                    onClick={() => removeRow(index)}
                                    title={t("objectRecognition.openVocabRemove")}
                                    aria-label={t("objectRecognition.openVocabRemoveAria", {
                                        index: index + 1,
                                    })}
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-r3 border border-red-900/80 bg-red-600 text-lg font-semibold leading-none text-white shadow-sm hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <span aria-hidden className="-mt-px block">
                                        ×
                                    </span>
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>

                <button
                    type="button"
                    disabled={promptsLoading}
                    onClick={addRow}
                    className="w-fit rounded-r5 border border-black80 bg-black80 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-black70 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {t("objectRecognition.openVocabAdd")}
                </button>

                <div className="flex flex-wrap gap-2 border-t border-black80 pt-3">
                    <button
                        type="button"
                        disabled={promptsLoading}
                        onClick={() => void loadPrompts()}
                        className="rounded-r5 border border-black80 bg-black80 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-black70 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {t("objectRecognition.openVocabReload")}
                    </button>
                    <button
                        type="button"
                        disabled={promptsSaving || promptsLoading}
                        onClick={() => void savePrompts()}
                        className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-primary1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {t("objectRecognition.openVocabSave")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OpenVocabPromptsPanel;
